<template>
  <div ref="tariffsRef">
    <div class="flex gap-xs flex-wrap items-stretch">
      <DbcBanner
        v-if="hasTariffError"
        id="errorBanner"
        :severity="'warn'"
        :message="$t('dbc-registration-tariff-error')"
      />
      <div
        v-else
        v-for="tariff in tariffs?.filter((tariff) => !tariff.hidden)"
        :key="tariff.id"
        :id="`tariff-${tariff.id}`"
        class="w-[18rem] max-mobile:w-full max-mobile:m-auto"
      >
        <TariffCard :tariff="tariff" @onSelect="selectTariff" />
      </div>
    </div>
    <RegistrationActionCode
      v-if="isActionCodeAllowed"
      @submitActionCode="submitActionCode"
      :isActionCodeLoading="isActionCodeLoading"
      :actionCodeError="actionCodeError"
    />
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  tariffId: string
  isActionCodeAllowed: boolean
}>()

const emit = defineEmits<{
  (e: 'selectTariff', tariff: Tariff): void
  (e: 'submitActionCode', actionCode: string): void
}>()

const { t, locale } = useI18n()

const route = useRoute()

const hasTariffError = ref(false)
const tariffs = ref<Tariff[] | null>(null)

const isActionCodeLoading = ref(false)
const actionCodeError = ref('')

async function getTariffs(actionCode?: string) {
  const response = await fetchTariffs(
    actionCode ? actionCode : props.tariffId,
    locale.value
  )

  if (response.status === 200 || response.status === 304) {
    tariffs.value = response._data.data
    hasTariffError.value = false
    actionCodeError.value = ''

    if (tariffs.value?.length === 1) {
      selectTariff(tariffs.value[0]!)
    }

    if (route.query.tariff && tariffs.value) {
      tariffs.value.forEach((tariff: Tariff) => {
        if (tariff.id === route.query.tariff) {
          setTimeout(() => {
            selectTariff(tariff)
          }, 500)
        }
      })
    }

    isActionCodeLoading.value = false
    if (actionCode) emit('submitActionCode', actionCode)
  } else if (response.data?.code === 14004 && isActionCodeLoading.value) {
    actionCodeError.value = t('dbc-error-special-tariff-id')
    hasTariffError.value = false
    isActionCodeLoading.value = false
    tariffsRef.value?.scrollIntoView(false)
  } else {
    hasTariffError.value = true
    isActionCodeLoading.value = false
  }
}

if (route.query.actionCode) {
  await getTariffs(route.query.actionCode as string)
} else {
  await getTariffs()
}

function selectTariff(selectedTariff: Tariff) {
  if (tariffs.value) {
    tariffs.value.forEach((tariff: Tariff) => {
      if (tariff.id !== selectedTariff.id) {
        tariff.hidden = true
      } else {
        tariff.selected = true
      }
    })
  }
  actionCodeError.value = ''
  emit('selectTariff', selectedTariff)
}

const tariffsRef = ref<HTMLElement | null>(null)

async function submitActionCode(actionCode: string) {
  isActionCodeLoading.value = true
  await nextTick()
  await getTariffs(actionCode)
}

function showAllTariffs() {
  if (tariffs.value) {
    tariffs.value.forEach((tariff: Tariff) => {
      tariff.hidden = false
      tariff.selected = false
    })
  }
}

defineExpose({
  showAllTariffs,
})
</script>
